import { useState, useEffect } from 'react';
import type { AfricanAirportsData } from "@/types/airport.types";

interface UseAfricanAirportsDataResult {
  data: AfricanAirportsData | null;
  loading: boolean;
  error: string | null;
}

let cache: AfricanAirportsData | null = null;

export function useAfricanAirportsData(): UseAfricanAirportsDataResult {
  const [data, setData] = useState<AfricanAirportsData | null>(cache);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If data is already cached, use it
    if (cache) {
      setData(cache);
      setLoading(false);
      setError(null);
      return;
    }

    // Fetch data if not cached
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch('/african_countries_cities_airports.json');

        if (!response.ok) {
          throw new Error(`Failed to fetch airport data: ${response.statusText}`);
        }

        const jsonData = await response.json();
        // Cache the data at module level
        cache = jsonData;
        setData(jsonData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error fetching airport data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, loading, error };
}

